/**
 * Universal hook for divisional chakra settings (D7, D8, D12, D20, D24, D30, D40, D60)
 * Single source of truth for all divisional chakra operations
 *
 * Composed from focused sub-hooks:
 * - useChakraVisibility: show/hide toggle + event emission
 * - useChakraStartPoint: start point management + event emission
 * - useChakraCustomSettings: custom settings (degrees, label) + event emission
 *
 * Features:
 * - Show/hide toggle
 * - Start point selection (sunrise, planets, lagna)
 * - Per-chart persistence
 * - Event emission
 * - Global init tracking with cleanup
 */

import { useState, useEffect, useCallback } from 'react';
import { useMultipleCharts } from '../components/common/AstrologyChart/MultipleChartsContext';
import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import {
  DivisionalChakraType,
  ChakraStartPoint,
  CustomStartPointSettings
} from '../config/divisionalChakraConfig';
import { useChakraVisibility } from './useChakraVisibility';
import { useChakraStartPoint } from './useChakraStartPoint';
import { useChakraCustomSettings } from './useChakraCustomSettings';

export interface DivisionalChakraSettings {
  showChakra: boolean;
  startPoint: ChakraStartPoint;
  customSettings: CustomStartPointSettings;
  isLoading: boolean;
  error: string | null;
  updateVisibility: (enabled: boolean) => Promise<void>;
  updateStartPoint: (startPoint: ChakraStartPoint) => Promise<void>;
  updateCustomSettings: (settings: CustomStartPointSettings) => Promise<void>;
  chartId: string | null;
  chakraType: DivisionalChakraType;
}

/**
 * GLOBAL initialization tracker - shared across ALL hook instances
 * Prevents duplicate event emissions when multiple components use the same hook
 * Format: { 'chart_id_chakraType': true }
 */
const globalInitializedCharts: Record<string, boolean> = {};

/**
 * Clean up global init tracker entry for a given chart+chakra combination
 * Called when chart is removed to prevent stale entries
 */
export const cleanupGlobalInitTracker = (chartId: string, chakraType?: DivisionalChakraType): void => {
  if (chakraType) {
    delete globalInitializedCharts[`${chartId}_${chakraType}`];
  } else {
    // Clean up all entries for this chart
    for (const key of Object.keys(globalInitializedCharts)) {
      if (key.startsWith(`${chartId}_`)) {
        delete globalInitializedCharts[key];
      }
    }
  }
};

/**
 * Custom hook for managing divisional chakra settings for the ACTIVE chart only
 * Composes useChakraVisibility, useChakraStartPoint, and useChakraCustomSettings
 *
 * @param chakraType - Type of divisional chakra (d7, d8, d12, etc.)
 */
export const useDivisionalChakraSettings = (
  chakraType: DivisionalChakraType
): DivisionalChakraSettings => {
  const { activeChartId } = useMultipleCharts();

  // Shared loading/error state passed to sub-hooks
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compose focused sub-hooks
  const visibility = useChakraVisibility(chakraType, isLoading, setIsLoading, setError);
  const startPointHook = useChakraStartPoint(chakraType, isLoading, setIsLoading, setError);
  const customSettingsHook = useChakraCustomSettings(chakraType, isLoading, setIsLoading, setError);

  // Wrap updateVisibility to pass current startPoint and customSettings
  const updateVisibility = useCallback(async (enabled: boolean): Promise<void> => {
    await visibility.updateVisibility(enabled, startPointHook.startPoint, customSettingsHook.customSettings);
  }, [visibility.updateVisibility, startPointHook.startPoint, customSettingsHook.customSettings]);

  // Wrap updateStartPoint to pass current showChakra
  const updateStartPoint = useCallback(async (newStartPoint: ChakraStartPoint): Promise<void> => {
    await startPointHook.updateStartPoint(newStartPoint, visibility.showChakra);
  }, [startPointHook.updateStartPoint, visibility.showChakra]);

  // Wrap updateCustomSettings to pass current showChakra and setStartPoint
  const updateCustomSettings = useCallback(async (newSettings: CustomStartPointSettings): Promise<void> => {
    await customSettingsHook.updateCustomSettings(newSettings, visibility.showChakra, startPointHook.setStartPoint);
  }, [customSettingsHook.updateCustomSettings, visibility.showChakra, startPointHook.setStartPoint]);

  // Sync state when activeChartId changes + global init tracking
  useEffect(() => {
    if (activeChartId) {
      // CRITICAL: Use GLOBAL tracker to prevent duplicate events across hook instances
      const chartKey = `${activeChartId}_${chakraType}`;

      if (globalInitializedCharts[chartKey]) {
        // Already initialized - just sync state, don't emit event
        const newSetting = visibility.getChakraSetting();
        const newStartPoint = startPointHook.getStartPointSetting();
        const newCustomSettings = customSettingsHook.getCustomSettings();

        if (newSetting !== visibility.showChakra) visibility.setShowChakra(newSetting);
        if (newStartPoint !== startPointHook.startPoint) startPointHook.setStartPoint(newStartPoint);
        customSettingsHook.setCustomSettings(newCustomSettings);
        setError(null);
        return; // Exit early - no event emission
      }

      // First load for this chart+chakraType - mark as initialized IMMEDIATELY
      globalInitializedCharts[chartKey] = true;

      const newSetting = visibility.getChakraSetting();
      const newStartPoint = startPointHook.getStartPointSetting();
      const newCustomSettings = customSettingsHook.getCustomSettings();

      if (newSetting !== visibility.showChakra) {
        visibility.setShowChakra(newSetting);
      }
      if (newStartPoint !== startPointHook.startPoint) {
        startPointHook.setStartPoint(newStartPoint);
      }
      customSettingsHook.setCustomSettings(newCustomSettings);
      setError(null);

      // Emit event on first load if chakra should be visible (restored from localStorage)
      if (newSetting === true) {
        globalEventStream.emit({
          id: `${chakraType}-restore-${activeChartId}-${Date.now()}`,
          type: `${chakraType}.chakra.toggle`,
          source: `divisional-chakra-settings-hook-${chakraType}-restore`,
          timestamp: Date.now(),
          chartId: activeChartId,
          data: {
            chartId: activeChartId,
            [`show${chakraType.toUpperCase()}Chakra`]: true,
            startPoint: newStartPoint,
            customSettings: newStartPoint === 'custom' ? newCustomSettings : undefined
          }
        });
      }
    } else {
      setError(null);
    }
  }, [
    activeChartId,
    chakraType,
    visibility.getChakraSetting,
    visibility.showChakra,
    visibility.setShowChakra,
    startPointHook.getStartPointSetting,
    startPointHook.startPoint,
    startPointHook.setStartPoint,
    customSettingsHook.getCustomSettings,
    customSettingsHook.setCustomSettings
  ]);

  // Cleanup global init tracker when chart is unloaded
  useEffect(() => {
    if (!activeChartId) return;

    const subscription = globalEventStream.subscribe('chart.unloaded', (event: any) => {
      const unloadedChartId = event?.data?.chartId || event?.chartId;
      if (unloadedChartId) {
        cleanupGlobalInitTracker(unloadedChartId, chakraType);
      }
    });

    return () => subscription?.unsubscribe?.();
  }, [activeChartId, chakraType]);

  return {
    showChakra: visibility.showChakra,
    startPoint: startPointHook.startPoint,
    customSettings: customSettingsHook.customSettings,
    isLoading,
    error,
    updateVisibility,
    updateStartPoint,
    updateCustomSettings,
    chartId: activeChartId,
    chakraType
  };
};
